import React from "react";
import { displayFormatDate, toggleMenu } from "../../utils/helpers";
import PageTitle from "../../components/PageTitle";
import Select from "react-select";
import { useState, useEffect } from "react";
import { ThreeDots } from "react-loader-spinner";
import {
  API_GET_RAISED_REQUESTS,
  API_GET_RAISED_CONCERN_EMPLOYEES_NAME,
  API_DOWNLOAD_RAISED_CONCERNS_REPORT,
} from "../../config/Endpoints";
import SortableTable from "../../components/SortableTable";
import Pagination from "../../components/Pagination";
import axios from "axios";
import Cookies from "js-cookie";
import NoRecordsAvailable from "../../components/NoRecordsAvailable";
import { TailSpin } from "react-loader-spinner";
import RaisedConcernActionModal from "./RaisedConcernActionModal";
import Badge from "../../components/Badge";
import { requestStatusOptions } from "../../constants/requestStatusOptions";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import SelectableSortableTable from "../../components/ProjectManagement/CSAT/SelectableSortabletable";

const RaisedConcernRequests = ({ title, description }) => {
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  const [limit, setLimit] = useState(10);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const accessToken = Cookies.get("accessToken");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isAllPagesSelected, setIsAllPagesSelected] = useState(false);

  const startOfMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    1,
  );
  const endOfMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth() + 1,
    0,
  );

  const [dateRange, setDateRange] = useState([startOfMonth, endOfMonth]);
  const [loadingExport, setLoadingExport] = useState(false);
  const [startDate, endDate] = dateRange;
  const isInvalidDateRange = (startDate && !endDate) || (!startDate && endDate);

  const columns = [
    {
      key: "empName",
      label: "Employee Name",
      sortable: true,
      render: (row) => row.empName,
    },
    {
      key: "concernRequest",
      label: "Raised Concern",
      sortable: true,
      render: (row) => row.concernRequest,
    },
    {
      key: "concernDate",
      label: "Concern Date",
      sortable: true,
      compare: (a, b) =>
        new Date(a._original.concernDate) - new Date(b._original.concernDate),
      render: (row) => row.concernDate,
    },
    {
      key: "punchIn",
      label: "Punch In",
      sortable: true,
      render: (row) => row.punchIn,
    },
    {
      key: "punchOut",
      label: "Punch Out",
      sortable: true,
      render: (row) => row.punchOut,
    },
    {
      key: "totalHours",
      label: "Total Hours",
      sortable: true,
      render: (row) => row.totalHours,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => (
        <Badge
          type={
            row.status === "PENDING"
              ? "warning"
              : row.status === "REJECTED"
                ? "danger"
                : "success"
          }
          className="text-capitalize"
        >
          {row.status === "PENDING"
            ? "Pending"
            : row.status === "REJECTED"
              ? "Rejected"
              : "Approved"}
        </Badge>
      ),
    },
  ];

  const handleSelectionChange = (ids) => {
    const currentPageIds = tableData.map((row) => row._original.id);

    // 1️⃣ Update selectedRows (add + remove properly)
    setSelectedRows((prev) => {
      const withoutCurrentPage = prev.filter(
        (id) => !currentPageIds.includes(id),
      );
      return [...withoutCurrentPage, ...ids];
    });

    // 2️⃣ Update selectedRequests accordingly
    setSelectedRequests((prev) => {
      const withoutCurrentPage = prev.filter(
        (item) => !currentPageIds.includes(item.id),
      );

      const selectedFromPage = tableData
        .filter((row) => ids.includes(row._original.id))
        .map((row) => row._original);

      return [...withoutCurrentPage, ...selectedFromPage];
    });
  };

  const handleSelectAllPages = async (checked) => {
    if (!checked) {
      clearSelection();
      return;
    }
    setIsAllPagesSelected(true);
    // Fetch all pending requests across all pages
    try {
      const params = {
        page: 0,
        size: totalRequests, // Fetch all records
      };

      if (selectedEmployee) params.empId = selectedEmployee.value;
      if (selectedStatus) params.status = selectedStatus;
      const fromDate = startDate && endDate ? startDate : startOfMonth;
      const toDate = startDate && endDate ? endDate : endOfMonth;

      params.startDate = fromDate.toLocaleDateString("en-CA");
      params.endDate = toDate.toLocaleDateString("en-CA");

      const res = await axios.get(API_GET_RAISED_REQUESTS, {
        params,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const allPendingRequests = res.data.data.content.filter(
        (item) => item.status === "PENDING",
      );

      setSelectedRequests(allPendingRequests);
      setSelectedRows(allPendingRequests.map((item) => item.id));
      setIsAllPagesSelected(true);
    } catch (error) {
      console.error("Error fetching all pending requests:", error);
    }
  };

  const clearSelection = () => {
    setSelectedRows([]);
    setSelectedRequests([]);
    setIsAllPagesSelected(false);
  };

  useEffect(() => {
    fetchConcernRequests();
  }, [currentPage, limit]);

  useEffect(() => {
    if (!selectedEmployee && !selectedStatus && !startDate && !endDate) {
      setCurrentPage(1);
      fetchConcernRequests();
    }
  }, [selectedEmployee, selectedStatus, startDate, endDate]);

  const fetchConcernRequests = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage - 1,
        size: limit,
      };

      if (selectedEmployee) params.empId = selectedEmployee.value;
      if (selectedStatus) params.status = selectedStatus;
      const fromDate = startDate && endDate ? startDate : startOfMonth;
      const toDate = startDate && endDate ? endDate : endOfMonth;

      params.startDate = fromDate.toLocaleDateString("en-CA");
      params.endDate = toDate.toLocaleDateString("en-CA");

      const res = await axios.get(API_GET_RAISED_REQUESTS, {
        params,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const formattedData = res.data.data.content.map((item) => ({
        empName: item.empName,
        concernRequest: item.reason,
        concernDate: displayFormatDate(item.concernDate),
        punchIn: item.punchInTime ?? "NA",
        punchOut: item.punchOutTime ?? "NA",
        totalHours: item.totalHour ?? "NA",
        status: item.status ?? "-",
        _original: item,
      }));

      setTableData(formattedData);
      setTotalPages(res.data.data.totalPages);
      setTotalRequests(res.data.data.totalElements);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployeeNames();
  }, []);

  const fetchEmployeeNames = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(API_GET_RAISED_CONCERN_EMPLOYEES_NAME, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const options = res.data.map((emp) => ({
        label: emp.fullName,
        value: emp.userId,
      }));

      setEmployeeOptions(options);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setCurrentPage(1);
  };
  const handleFilterClick = () => {
    setCurrentPage(1);
    clearSelection();
    fetchConcernRequests();
  };
  const handleExportClick = async () => {
    setLoadingExport(true);
    try {
      const params = {};

      if (selectedEmployee) params.employeeId = selectedEmployee.value;
      if (selectedStatus) params.status = selectedStatus;

      const fromDate = startDate && endDate ? startDate : startOfMonth;
      const toDate = startDate && endDate ? endDate : endOfMonth;

      params.startDate = fromDate.toLocaleDateString("en-CA");
      params.endDate = toDate.toLocaleDateString("en-CA");

      const res = await axios.get(API_DOWNLOAD_RAISED_CONCERNS_REPORT, {
        params,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        responseType: "blob",
      });

      const blob = new Blob([res.data]);
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "Raised_Concerns_Report.xlsx";
      document.body.appendChild(link);

      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      if (error.response?.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          const data = JSON.parse(reader.result);
          toast.error(data.message);
        };
        reader.readAsText(error.response.data);
      } else {
        toast.error(error.response?.data?.message);
      }
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <>
      <div className="wrapper-body">
        {isLoading && (
          <div className="overlay">
            <div className="mt-5">
              <TailSpin
                height="100"
                width="100"
                color="blue"
                ariaLabel="tail-spin-loading"
                visible={true}
              />
            </div>
          </div>
        )}
        <div
          className="modal fade"
          id="raisedConcernActionModal"
          data-bs-backdrop="static"
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <RaisedConcernActionModal
                selectedRequests={selectedRequests}
                fetchConcernRequests={fetchConcernRequests}
                clearSelection={clearSelection}
              />
            </div>
          </div>
        </div>
        <div className="page-heading">
          <div
            id="showMenuBtn"
            className="collapse-button"
            onClick={toggleMenu}
          >
            <span></span>
            <span></span>
            <span></span>
          </div>
          <PageTitle title={title} description={description} />
          <div className="total">
            Total Requests : <span>{totalRequests}</span>
          </div>
        </div>

        <div className="filters d-flex  gap-2 mb-3 col-md-12">
          <div className="filter ">
            <label className="label">Employee Name</label>
            <Select
              className="pr"
              placeholder="Select Employee Name"
              isClearable
              options={employeeOptions}
              value={selectedEmployee}
              onChange={(option) => {
                setSelectedEmployee(option);
                setCurrentPage(1);
              }}
            />
          </div>
          <div className="filter">
            <label className="label">Status</label>
            <Select
              className="pr"
              placeholder="Select Status"
              isClearable
              options={requestStatusOptions}
              value={
                requestStatusOptions.find(
                  (status) => status.value === selectedStatus,
                ) || null
              }
              onChange={(option) =>
                setSelectedStatus(option ? option.value : null)
              }
            />
          </div>
          <div className="filter">
            <label className="label">Date Range</label>
            <DatePicker
              className="pr datepicker"
              toggleCalendarOnIconClick
              placeholderText="mm/dd/yyyy - mm/dd/yyyy"
              selectsRange={true}
              startDate={startDate}
              endDate={endDate}
              isClearable={startDate && endDate}
              onChange={(update) => setDateRange(update)}
              showIcon
              required
            />
          </div>

          <button
            className={
              isInvalidDateRange ? "theme-button disabled" : "theme-button"
            }
            disabled={isInvalidDateRange}
            onClick={handleFilterClick}
          >
            Filter
          </button>
          <button
            className={
              loadingExport || isInvalidDateRange
                ? "theme-button disabled"
                : "theme-button"
            }
            onClick={handleExportClick}
            disabled={loadingExport || isInvalidDateRange}
          >
            {loadingExport ? (
              <ThreeDots height="20" width="40" radius="3" color="white" />
            ) : (
              "Export"
            )}
          </button>
          {selectedRows.length > 0 && (
            <button
              className="theme-button bg-success ms-auto"
              data-bs-toggle="modal"
              data-bs-target="#raisedConcernActionModal"
            >
              Next
            </button>
          )}
        </div>
        {!isLoading &&
          (tableData && tableData.length > 0 ? (
            <>
              <SelectableSortableTable
                columns={columns}
                data={tableData}
                selectedRows={selectedRows}
                onSelectionChange={handleSelectionChange}
                getRowId={(row) => row._original.id}
                isRowSelectable={(row) => row._original.status === "PENDING"}
              />

              <div className="row">
                <div className="col-md-3">
                  <div>
                    {/* Dropdown for selecting items per page */}
                    <label htmlFor="itemsPerPage">Items per page </label>
                    <select
                      id="itemsPerPage"
                      onChange={handleLimitChange}
                      value={limit}
                      className="border ms-3 w-25  p-2"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={30}>30</option>
                      <option value={40}>40</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-9">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className=" text-center">
              <NoRecordsAvailable />
            </div>
          ))}
      </div>
    </>
  );
};

export default RaisedConcernRequests;
